import * as THREE from "three";
import type { UnitLook } from "../unitFx";

/**
 * The rare creature's shine (M36 §5, "sparkle and glow like a shiny Pokemon"), on top of whatever model it wears —
 * ART's `enemy_rare_cN` once it ships, the tinted boss stand-in until then:
 *
 * - the body: the unit uber-shader's shiny layer (unitFxShader.ts SHINY_GLSL) — iridescent sheen, glitter, and a glow
 *   breathing at ~0.8 Hz, which also lifts its `_glow` slots (the basic patch). No extra draw: the uniforms are the
 *   view's own (UnitLook);
 * - around it, one `THREE.Points` draw (additive, no depth write, a procedural four-point star in the fragment shader,
 *   no texture): `Motes` orbiting at three heights and radii, and a `Trail` of sparkles shed behind it as it walks,
 *   each living `TrailLife` s, rising and shrinking as it twinkles out.
 *
 * Budget (iPhone 15 Pro, rare on screen): +1 draw call, 72 point sprites (≈ 20 px each at play zoom, additive over
 * the body — the overdraw of a single boss's dust burst), a branch on a uniform for every other unit's fragments.
 * Reduced effects: the sheen and glow stay (they are the creature's look), the glitter, motes and trail go.
 */
export class RareShiny {
  static readonly Motes = 24;
  static readonly Trail = 48;
  static readonly TrailLife = 1.1;
  /** World-space sprite size (cells) of a mote / a fresh trail sparkle. */
  static readonly MoteSize = 0.11;
  static readonly TrailSize = 0.09;
  /** Seconds between trail sparkles while it moves (and half as often while it stands). */
  static readonly TrailEvery = 0.035;
  /**
   * The rare's rim (its sparkle colour instead of the enemy red): strength and boss-style pulse. Moderate: the rim is
   * added light, and at 0.6 its pale gold/ice veiled a dark body's silhouette.
   */
  static readonly RimStrength = 0.35;
  static readonly RimPulse = 0.15;

  readonly points: THREE.Points;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly size: Float32Array;
  private readonly geo: THREE.BufferGeometry;
  private readonly mat: THREE.ShaderMaterial;
  /** Trail ring: birth time, position, drift per sparkle. */
  private readonly born = new Float32Array(RareShiny.Trail).fill(-99);
  private readonly tpos = new Float32Array(RareShiny.Trail * 3);
  private readonly tdrift = new Float32Array(RareShiny.Trail * 3);
  private next = 0;
  private emitIn = 0;
  private time = 0;
  /** 0..1, eased in on spawn and out as it dies (the motes fade with the body's dissolve). */
  private presence = 0;
  /** The creature's shine strength (RareDef.shine): scales the sheen, glints and glow pulse on its body. */
  private strength = 1;
  private readonly a = new THREE.Color();
  private readonly b = new THREE.Color();
  private readonly last = new THREE.Vector3(NaN, 0, 0);
  /**
   * Per showpiece (reset by `begin`): the body's glitter (0 off), how far the motes orbit (× its height) and whether
   * this layer sheds its own trail. The Starfall Wyrm (showpiece.ts) turns glitter and shedding off and sends the motes
   * out past its wingtips: its own comet trail streams behind it, and nothing sparkles on top of the model.
   */
  glitter = 1;
  orbit = 1;
  shed = true;

  constructor() {
    const n = RareShiny.Motes + RareShiny.Trail;
    this.pos = new Float32Array(n * 3);
    this.col = new Float32Array(n * 3);
    this.size = new Float32Array(n);
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("color", new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("size", new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);   // it follows the rare: never culled
    this.mat = sparkleMaterial();
    this.points = new THREE.Points(this.geo, this.mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 4;
    this.points.visible = false;
  }

  /** The rare's colours (0xRRGGBB): set the shine's tint on its look and the motes' palette. */
  begin(look: UnitLook | null, sparkA: number, sparkB: number, strength = 1): void {
    this.a.setHex(sparkA); this.b.setHex(sparkB);
    this.strength = strength;
    if (look) {
      look.u.uTdShinyA.value.setHex(sparkA);
      look.u.uTdShinyB.value.setHex(sparkB);
      // a golden rim instead of the enemy red: it reads as treasure, not threat
      look.rimColor.setHex(sparkA); look.rimStrength = RareShiny.RimStrength; look.rimPulse = RareShiny.RimPulse;
    }
    this.presence = 0;
    this.glitter = 1; this.orbit = 1; this.shed = true;
    this.born.fill(-99);
    this.last.set(NaN, 0, 0);
    this.points.visible = true;
  }

  /**
   * Per frame for the living (or dying, `fade` < 1) rare: its look's shine uniforms and the sprites around it. `feet` is
   * where it is drawn, `height` its drawn height (world).
   */
  frame(look: UnitLook | null, feet: THREE.Vector3, height: number, dt: number, fade: number, reduced: boolean): void {
    this.time += dt;
    const t = this.time;
    this.presence = Math.min(1, this.presence + dt / 0.8) * Math.max(0, Math.min(1, fade));
    const pr = this.presence;
    if (look) {
      const pulse = 0.5 + 0.5 * Math.sin(t * Math.PI * 2 * 0.8);
      // y = glitter cells per body height (0 = none): sized for the screen (glitterCells), so at the game camera the
      // body flashes with a few glints a few pixels across instead of sub-pixel cells only the close-ups showed
      look.u.uTdShiny.value.set(pr * this.strength, reduced ? 0 : this.glitter * glitterCells(height, SHINY_SCREEN.pxPerUnit), pulse * pr, t * 0.12);
    }
    const p = this.pos, c = this.col, s = this.size;
    const h = Math.max(0.3, height);
    if (reduced) { this.points.visible = false; return; }
    this.points.visible = pr > 0.01;
    // motes: three rings (low, middle, crown), each mote bobbing and twinkling on its own phase
    for (let i = 0; i < RareShiny.Motes; i++) {
      const ring = i % 3, k = i / RareShiny.Motes;
      const r = h * (0.55 + 0.18 * ring) * this.orbit, speed = (ring === 1 ? -1 : 1) * (0.9 + 0.25 * ring);
      const ang = k * Math.PI * 2 * 3 + t * speed;
      const y = h * (0.25 + 0.38 * ring) + Math.sin(t * 2.3 + i * 1.7) * h * 0.06;
      p[i * 3] = feet.x + Math.cos(ang) * r; p[i * 3 + 1] = feet.y + y; p[i * 3 + 2] = feet.z + Math.sin(ang) * r;
      const tw = 0.55 + 0.45 * Math.sin(t * 6.0 + i * 2.39);
      const mix = 0.5 + 0.5 * Math.sin(i * 1.3 + t * 0.7);
      c[i * 3] = (this.a.r * (1 - mix) + this.b.r * mix) * 1.8 * tw * pr;
      c[i * 3 + 1] = (this.a.g * (1 - mix) + this.b.g * mix) * 1.8 * tw * pr;
      c[i * 3 + 2] = (this.a.b * (1 - mix) + this.b.b * mix) * 1.8 * tw * pr;
      s[i] = RareShiny.MoteSize * (0.6 + 0.4 * tw) * Math.max(1, h / 0.8);
    }
    // trail: shed from the body as it moves (fewer while it stands)
    const moved = Number.isNaN(this.last.x) ? 0 : Math.hypot(feet.x - this.last.x, feet.z - this.last.z);
    this.last.copy(feet);
    this.emitIn -= dt;
    while (this.shed && this.emitIn <= 0 && pr > 0.2) {
      this.emitIn += moved > 1e-4 ? RareShiny.TrailEvery : RareShiny.TrailEvery * 2.5;
      const j = this.next; this.next = (this.next + 1) % RareShiny.Trail;
      const u = Math.random() * Math.PI * 2, rr = h * (0.15 + 0.35 * Math.random());
      this.tpos[j * 3] = feet.x + Math.cos(u) * rr; this.tpos[j * 3 + 1] = feet.y + h * (0.15 + 0.7 * Math.random()); this.tpos[j * 3 + 2] = feet.z + Math.sin(u) * rr;
      this.tdrift[j * 3] = (Math.random() - 0.5) * 0.15; this.tdrift[j * 3 + 1] = 0.25 + Math.random() * 0.35; this.tdrift[j * 3 + 2] = (Math.random() - 0.5) * 0.15;
      this.born[j] = t;
    }
    for (let j = 0; j < RareShiny.Trail; j++) {
      const i = RareShiny.Motes + j, age = t - this.born[j];
      if (age < 0 || age > RareShiny.TrailLife) { s[i] = 0; continue; }
      const u = age / RareShiny.TrailLife;
      p[i * 3] = this.tpos[j * 3] + this.tdrift[j * 3] * age;
      p[i * 3 + 1] = this.tpos[j * 3 + 1] + this.tdrift[j * 3 + 1] * age;
      p[i * 3 + 2] = this.tpos[j * 3 + 2] + this.tdrift[j * 3 + 2] * age;
      const tw = 0.6 + 0.4 * Math.sin(t * 22 + j * 3.1);
      const k = (1 - u) * tw * 2.2;
      const m = j % 2 === 0 ? this.a : this.b;
      c[i * 3] = m.r * k; c[i * 3 + 1] = m.g * k; c[i * 3 + 2] = m.b * k;
      s[i] = RareShiny.TrailSize * (1 - 0.6 * u) * Math.max(1, h / 0.8);
    }
    (this.geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.size as THREE.BufferAttribute).needsUpdate = true;
  }

  /** The rare is gone (dissolved, leaked, the level over): the sprites go; the look is reset with its pooled view. */
  end(): void { this.points.visible = false; this.presence = 0; }

  dispose(): void { this.geo.dispose(); this.mat.dispose(); }
}

/**
 * Additive point sprites with a procedural sparkle: a soft core and a thin four-point star, the arms twinkling by the
 * sprite's own brightness. Size in world units (attribute `size`), perspective or orthographic.
 */
export function sparkleMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uPx: SPARKLE_PX },
    vertexShader: /* glsl */ `
      attribute float size;
      attribute vec3 color;
      uniform float uPx;
      varying vec3 vC;
      void main() {
        vC = color;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mv;
        float persp = projectionMatrix[2][3] == 0.0 ? 1.0 : 1.0 / max(0.01, -mv.z);
        gl_PointSize = size <= 0.0 ? 0.0 : clamp(size * projectionMatrix[1][1] * uPx * persp, 0.0, 64.0);
      }`,
    fragmentShader: /* glsl */ `
      varying vec3 vC;
      void main() {
        vec2 q = gl_PointCoord * 2.0 - 1.0;
        float r = length(q);
        float core = exp(-r * r * 9.0);
        float star = max(exp(-abs(q.x) * 18.0) * (1.0 - abs(q.y)), exp(-abs(q.y) * 18.0) * (1.0 - abs(q.x)));
        float a = core + 0.7 * star;
        if (a < 0.01) discard;
        gl_FragColor = vec4(vC * a, 1.0);
      }`,
    transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending, toneMapped: false,
  });
}

/**
 * The body glitter's cells per body height for a creature `height` world units tall drawn at `pxPerUnit` drawing-buffer
 * pixels per world unit: cells about `GLITTER_PX` pixels across, between `GLITTER_MIN` (far: fewer, bigger glints) and
 * `GLITTER_MAX` (the close-up's fine sparkle, v1's fixed 38). Unknown scale (0): the fine grain.
 */
export function glitterCells(height: number, pxPerUnit: number): number {
  if (!(pxPerUnit > 0)) return GLITTER_MAX;
  return Math.max(GLITTER_MIN, Math.min(GLITTER_MAX, (Math.max(0.3, height) * pxPerUnit) / GLITTER_PX));
}
export const GLITTER_PX = 12;
export const GLITTER_MIN = 8;
export const GLITTER_MAX = 38;
/** Drawing-buffer pixels per world unit at the showpiece (rareMoment.ts keeps it current from the camera; 0 = unknown). */
export const SHINY_SCREEN = { pxPerUnit: 0 };

/**
 * Half the drawing buffer's height in px: the sprite shaders scale world size to pixels by it. The rig keeps it current
 * (`SceneRig` resize); shared by every sparkle material.
 */
export const SPARKLE_PX = { value: 600 };
